import React, {useState} from "react";
import {ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip} from "chart.js";
import {GraphType, TreeNode} from "../types/types";
import {Bar, Pie} from "react-chartjs-2";
import GraphButtons from "./GraphButtons";
import "./componentsCSS/Dashboard.css";
import {
    AGE_GRAPH_X_AXIS_TITLE,
    AGE_GRAPH_Y_AXIS_TITLE,
    DASHBOARD_INSIGHT_TITLE,
    DASHBOARD_SELECT_NODE_TEXT,
    DASHBOARD_SELECT_TEXT,
    DASHBOARD_SELECTED_NODE_TYPE_PREFIX,
    DASHBOARD_SELECTED_PARTICIPANT_COUNT_PREFIX,
    DASHBOARD_SELECTED_PREFIX
} from "../consts/consts";
import SelectedItemPath from "./SelectedItemPath";


ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);


interface DashboardProps {
    selectedNode: TreeNode | null;
    handleSelect: (newValue: string) => void;
}

const Dashboard: React.FC<DashboardProps> = ({selectedNode, handleSelect}) => {
    const [selectedGraph, setSelectedGraph] = useState<string>(GraphType.Age);


    const ageChartData = {
        labels: Object.keys(selectedNode?.age_distribution ?? {}),
        datasets: [
            {
                data: Object.values(selectedNode?.age_distribution ?? {}),
                backgroundColor: "rgba(75, 192, 192, 0.6)",
                borderColor: "rgba(75, 192, 192, 1)",
                borderWidth: 1,
            },
        ],
    };

    const genderChartData = {
        labels: Object.keys(selectedNode?.gender_distribution ?? {}),
        datasets: [
            {
                data: Object.values(selectedNode?.gender_distribution ?? {}),
                backgroundColor: ["#F8AE54", "#AFDC8F"],
                borderWidth: 1,
            },
        ],
    };

    const chartOptionsGender = {
        responsive: true,
        maintainAspectRatio: false, // Allow it to resize properly

        plugins: {
            legend: {
                display: true,
                position: 'bottom' as const,
                labels: {
                    padding: 20,
                },
            },
        },
    }

    const isMobile = window.innerWidth < 500
    const responsiveSizeHack = isMobile ? window.innerWidth + 400 : window.innerWidth

    const chartOptionsAge = {
        responsive: true,
        plugins: {
            legend: {
                display: false,
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: AGE_GRAPH_X_AXIS_TITLE,
                },
            },
            y: {
                title: {
                    display: true,
                    text: AGE_GRAPH_Y_AXIS_TITLE,
                },
            },
        },
    }


    return (
        <div className="insights-container">
            <div className="insights-title">{DASHBOARD_INSIGHT_TITLE}</div>

            <p>{DASHBOARD_SELECT_TEXT}</p>
            {!selectedNode ? (
                    <div className="insights-select-node-title">{DASHBOARD_SELECT_NODE_TEXT}</div>
                ) :
                (
                    <div className="selected-node-container">
                        <div className="selected-title">
                            <b>{DASHBOARD_SELECTED_PREFIX}</b>
                            {selectedNode.value.replace(/\s*\(.*\)$/, '')}
                        </div>
                        <div
                            className="selected-title">
                            <b>{DASHBOARD_SELECTED_NODE_TYPE_PREFIX}</b>{selectedNode.node_type}</div>
                        <div
                            className="selected-title">
                            <b>{DASHBOARD_SELECTED_PARTICIPANT_COUNT_PREFIX}</b>{selectedNode.unique_count}
                        </div>
                        <SelectedItemPath selectedNode={selectedNode} handleSelect={handleSelect}/>
                        <hr/>

                        <GraphButtons selectedGraph={selectedGraph} setSelectedGraph={setSelectedGraph}/>
                        {
                            selectedGraph === GraphType.Age ? (
                                <div className="graph-breakdown"><
                                    Bar data={ageChartData} options={chartOptionsAge}/>
                                </div>
                            ) : (
                                <div className="graph-breakdown">
                                    <Pie data={genderChartData}
                                         height={responsiveSizeHack}
                                         width={responsiveSizeHack}
                                         options={chartOptionsGender}/>
                                </div>
                            )
                        }
                    </div>
                )}
        </div>
    );
};

export default Dashboard;